import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { User, Session } from '@supabase/supabase-js';
import { loginSchema, signupSchema, resetPasswordSchema } from "@/lib/validationSchemas";
import { checkRateLimit, getUserFingerprint } from "@/lib/rateLimiting";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { MFAChallenge } from "@/components/MFAChallenge";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [showEmailReminder, setShowEmailReminder] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // A password-recovery link should always land on the dedicated reset page,
  // even if it gets bounced back to /auth by the auth redirect settings.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    if (hash.includes("type=recovery") || search.includes("type=recovery")) {
      navigate(`/reset-password${search}${hash}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    /**
     * USER JOURNEY: Authentication Flow
     * 
     * 1. User lands on /auth page
     * 2. After successful login/signup, check their profile status
     * 3. Route based on onboarding completion:
     *    - No profile or incomplete onboarding → /profile-setup
     *    - Onboarding completed → / (home page)
     * 
     * This ensures all authenticated users complete their profile before
     * accessing the main app.
     */
    const checkAuthAndRedirect = async (userId: string) => {
      try {
        // If MFA is required to reach aal2, show the challenge instead of redirecting.
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2") {
          const { data: factorList } = await supabase.auth.mfa.listFactors();
          const totp = factorList?.totp?.find((f) => f.status === "verified");
          if (totp) {
            setMfaFactorId(totp.id);
            return;
          }
        }

        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("user_id", userId)
          .maybeSingle();

        if (error) {
          console.error("Error checking profile:", error);
          navigate("/profile-setup");
          return;
        }

        // If no profile or onboarding not completed, go to profile setup
        if (!data || !data.onboarding_completed) {
          navigate("/profile-setup");
        } else {
          // Onboarding completed - redirect to home page
          navigate("/");
        }
      } catch (error) {
        console.error("Error in auth check:", error);
        navigate("/profile-setup");
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            checkAuthAndRedirect(session.user.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkAuthAndRedirect(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowEmailReminder(false);
    setValidationErrors({});

    try {
      // Validate input
      const schema = isLogin ? loginSchema : signupSchema;
      const validation = schema.safeParse({ email, password });

      if (!validation.success) {
        const errors: Record<string, string> = {};
        validation.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
        setLoading(false);
        return;
      }

      // Check rate limit
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const userId = currentUser?.id || getUserFingerprint();
      const action = isLogin ? "auth_login" : "auth_signup";
      
      const rateLimitCheck = await checkRateLimit(userId, action);
      if (!rateLimitCheck.allowed) {
        throw new Error(rateLimitCheck.error);
      }

      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Enhanced error messages
          if (error.message.includes("Invalid login credentials")) {
            throw new Error("Invalid email or password. Please check your credentials and try again.");
          } else if (error.message.includes("Email not confirmed")) {
            throw new Error("Please verify your email address before signing in. Check your inbox for the verification link.");
          } else {
            throw error;
          }
        }
        
        toast({
          title: "Welcome back!",
          description: "Successfully signed in.",
        });
      } else {
        // After signup, the confirmation link lands on the auth callback,
        // which hydrates the session and forwards to profile setup.
        const redirectUrl = `${window.location.origin}/auth/callback`;
        
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl
          }
        });

        if (error) {
          // Enhanced error messages
          if (error.message.includes("already registered")) {
            throw new Error("This email is already registered. Try signing in instead.");
          } else if (error.message.includes("password")) {
            throw new Error("Password is too weak. Use at least 6 characters with a mix of letters and numbers.");
          } else {
            throw error;
          }
        }

        // Show email verification reminder
        setShowEmailReminder(true);

        toast({
          title: "Account created! 🎉",
          description: "Check your email to verify your account.",
          duration: 6000,
        });
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      toast({
        title: isLogin ? "Sign in failed" : "Sign up failed",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    try {
      // Validate email
      const validation = resetPasswordSchema.safeParse({ email });
      if (!validation.success) {
        const errors: Record<string, string> = {};
        validation.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
        setLoading(false);
        return;
      }

      // Check rate limit
      const fingerprint = getUserFingerprint();
      const rateLimitCheck = await checkRateLimit(fingerprint, "auth_password_reset");
      if (!rateLimitCheck.allowed) {
        throw new Error(rateLimitCheck.error);
      }

      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        if (error.message.includes("not found")) {
          throw new Error("No account found with this email address.");
        }
        throw error;
      }

      toast({
        title: "Reset email sent ✉️",
        description: "Check your email for the password reset link. It may take a few minutes to arrive.",
        duration: 6000,
      });
      
      setShowResetPassword(false);
      setEmail("");
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        title: "Password reset failed",
        description: error.message || "Failed to send reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {mfaFactorId
              ? "Two-Factor Verification"
              : showResetPassword
              ? "Never mind — brain fog, we can help"
              : isLogin
              ? "Welcome Back"
              : "Create Account"}
          </CardTitle>
          <CardDescription>
            {mfaFactorId
              ? "Enter the code from your authenticator app to finish signing in."
              : showResetPassword
              ? "It happens to all of us. Pop in your email and we'll send you a link to set a new password."
              : isLogin
              ? "Sign in to continue your wellness journey"
              : "Start your personalized menopause wellness journey"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mfaFactorId ? (
            <MFAChallenge
              factorId={mfaFactorId}
              onVerified={async () => {
                setMfaFactorId(null);
                const { data: { user: u } } = await supabase.auth.getUser();
                if (u) {
                  const { data } = await supabase
                    .from("profiles")
                    .select("onboarding_completed")
                    .eq("user_id", u.id)
                    .maybeSingle();
                  navigate(data?.onboarding_completed ? "/" : "/profile-setup");
                }
              }}
              onCancel={() => setMfaFactorId(null)}
            />
          ) : (
            <>

          {/* Email Verification Reminder */}
          {showEmailReminder && !showResetPassword && (
            <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <h3 className="font-semibold text-sm mb-1">📧 Verify Your Email</h3>
              <p className="text-xs text-muted-foreground">
                We've sent a verification link to <strong>{email}</strong>. 
                Click the link in the email to activate your account and complete setup.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Don't see it? Check your spam folder or{" "}
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailReminder(false);
                    setEmail("");
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  try again
                </button>
              </p>
            </div>
          )}

          {showResetPassword ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.replace(/\s/g, ""));
                    setValidationErrors({});
                  }}
                  required
                  className={validationErrors.email ? "border-destructive" : ""}
                />

                {validationErrors.email && (
                  <p className="text-sm text-destructive">{validationErrors.email}</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowResetPassword(false);
                  setEmail("");
                }}
              >
                Back to Sign In
              </Button>
            </form>
          ) : (
            <>
              <form onSubmit={handleAuth} className="space-y-4">
                {/* Validation Errors Alert */}
                {Object.keys(validationErrors).length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please fix the errors below before continuing.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      // Strip whitespace/invisible characters that break native email validation
                      setEmail(e.target.value.replace(/\s/g, ""));
                      setValidationErrors({});
                    }}
                    required
                    className={validationErrors.email ? "border-destructive" : ""}
                  />

                  {validationErrors.email && (
                    <p className="text-sm text-destructive">{validationErrors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(true)}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setValidationErrors({});
                    }}
                    required
                    minLength={6}
                    className={validationErrors.password ? "border-destructive" : ""}
                  />
                  {validationErrors.password && (
                    <p className="text-sm text-destructive">{validationErrors.password}</p>
                  )}
                  {!isLogin && (
                    <p className="text-xs text-muted-foreground">
                      At least 6 characters with a mix of letters and numbers
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Loading..." : isLogin ? "Sign In" : "Sign Up"}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  {isLogin
                    ? "Don't have an account? Sign up"
                    : "Already have an account? Sign in"}
                </button>
              </div>
            </>
          )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
